import { FC, useState } from 'react';
import { Button, Dropdown, TextInput } from '@carbon/react';
import { Add, TrashCan, Undo } from '@carbon/react/icons';
import { NumericFormat, type OnValueChange } from 'react-number-format';
import type { CostClass } from './types-cost';
import {
  CLASS_DROPDOWN_ITEMS,
  CLASS_NONE_LABEL,
  classLabel,
  SUB_CATEGORY_NONE,
  subCategoryDropdownItems,
  type DraftLineItem,
} from './cost-utils';
import styles from './cost.module.scss';

interface Props {
  /**
   * Draft line items to render, INCLUDING items marked `destroyed`. A destroyed
   * item is shown struck through with its inputs disabled and an Undo control in
   * place of Remove; restoring just flips `destroyed` back off via `setItem`.
   */
  items: DraftLineItem[];
  /** Category dropdown options (canonical set ∪ draft categories). */
  categoryOptions: string[];
  /** Apply a partial patch to a single draft row, keyed by `item.key`. */
  setItem: (key: string, patch: Partial<DraftLineItem>) => void;
  /** Add a new draft line item under the given category. */
  onAddLineItem: (category: string) => void;
  /** Remove (or mark destroyed) the given draft line item. */
  onDeleteLineItem: (item: DraftLineItem) => void;
}

interface CurrencyCellProps {
  id: string;
  labelText: string;
  hideLabel: boolean;
  disabled: boolean;
  value: number | null;
  onValueChange: OnValueChange;
}

// A single `$`-prefixed, 2-decimal currency cell for the ledger.
//
// `fixedDecimalScale` is toggled OFF while the field is focused and back ON once
// it blurs. With it always on, the padded cents (`$0.00`) make the caret land
// past the two decimals, so any digit typed at the end is treated as a 3rd
// decimal and silently dropped — i.e. you can't edit the cents by clicking in
// and typing. Dropping it only while focused lets the decimals be edited
// naturally; restoring it on blur re-pads to two decimals so the resting display
// still matches the read-mode `formatCurrency` output.
//
// Each cell owns its own `focused` flag, which is why this is a component rather
// than inline markup: the ledger renders rows via `items.map`, and a hook can't
// be called inside that loop.
const CurrencyCell: FC<CurrencyCellProps> = ({
  id,
  labelText,
  hideLabel,
  disabled,
  value,
  onValueChange,
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <NumericFormat
      type="text"
      customInput={TextInput}
      id={id}
      labelText={labelText}
      hideLabel={hideLabel}
      size="sm"
      placeholder="—"
      disabled={disabled}
      thousandSeparator
      prefix="$"
      decimalScale={2}
      fixedDecimalScale={!focused}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      value={value}
      onValueChange={onValueChange}
    />
  );
};

// ── Flat ledger editor (edit mode — v2 column set) ──
export const CostLedgerEditor: FC<Props> = ({
  items,
  categoryOptions,
  setItem,
  onAddLineItem,
  onDeleteLineItem,
}) => {
  return (
    <div className={styles.ledger}>
      {items.length > 0 ? (
        <div className={styles.ledgerTable}>
          {items.map((item, idx) => {
            const showLabels = idx === 0;
            const classSelected = item.costClass ?? CLASS_NONE_LABEL;
            const isDestroyed = item.destroyed;
            return (
              <div
                key={item.key}
                className={`${styles.ledgerRow}${
                  isDestroyed ? ` ${styles.ledgerRowRemoved}` : ''
                }`}
              >
                <div className={styles.ledgerCell}>
                  <TextInput
                    id={`cost-code-${item.key}`}
                    labelText={showLabels ? 'Code' : ''}
                    hideLabel={!showLabels}
                    size="sm"
                    placeholder="Code"
                    disabled={isDestroyed}
                    value={item.code ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setItem(item.key, { code: e.target.value || null })
                    }
                  />
                </div>
                <div className={styles.ledgerCell}>
                  <TextInput
                    id={`cost-desc-${item.key}`}
                    labelText={showLabels ? 'Description' : ''}
                    hideLabel={!showLabels}
                    size="sm"
                    placeholder="Description"
                    disabled={isDestroyed}
                    value={item.description ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setItem(item.key, { description: e.target.value })
                    }
                  />
                </div>
                <div className={styles.ledgerCell}>
                  <Dropdown
                    id={`cost-class-${item.key}`}
                    titleText={showLabels ? 'Class' : ''}
                    hideLabel={!showLabels}
                    label="Select…"
                    size="sm"
                    disabled={isDestroyed}
                    items={CLASS_DROPDOWN_ITEMS}
                    selectedItem={classSelected}
                    itemToString={(i: CostClass | typeof CLASS_NONE_LABEL) =>
                      classLabel(i)
                    }
                    onChange={({
                      selectedItem,
                    }: {
                      selectedItem: CostClass | typeof CLASS_NONE_LABEL | null;
                    }) => {
                      if (selectedItem === null) return;
                      setItem(item.key, {
                        costClass:
                          selectedItem === CLASS_NONE_LABEL
                            ? null
                            : selectedItem,
                      });
                    }}
                  />
                </div>
                <div className={styles.ledgerCell}>
                  <Dropdown
                    id={`cost-category-${item.key}`}
                    titleText={showLabels ? 'Category' : ''}
                    hideLabel={!showLabels}
                    label="Select…"
                    size="sm"
                    disabled={isDestroyed}
                    items={categoryOptions}
                    selectedItem={item.category}
                    itemToString={(i: string | null) => i ?? ''}
                    onChange={({
                      selectedItem,
                    }: {
                      selectedItem: string | null;
                    }) => {
                      if (selectedItem) {
                        setItem(item.key, {
                          category: selectedItem,
                          subCategory: null,
                        });
                      }
                    }}
                  />
                </div>
                <div className={styles.ledgerCell}>
                  <Dropdown
                    id={`cost-subcat-${item.key}`}
                    titleText={showLabels ? 'Sub-Category' : ''}
                    hideLabel={!showLabels}
                    label="Select…"
                    size="sm"
                    disabled={isDestroyed}
                    items={subCategoryDropdownItems(
                      item.category,
                      item.subCategory
                    )}
                    selectedItem={item.subCategory?.trim() || SUB_CATEGORY_NONE}
                    itemToString={(i: string | null) => i ?? ''}
                    onChange={({
                      selectedItem,
                    }: {
                      selectedItem: string | null;
                    }) => {
                      if (selectedItem !== null) {
                        setItem(item.key, {
                          subCategory:
                            selectedItem === SUB_CATEGORY_NONE
                              ? null
                              : selectedItem,
                        });
                      }
                    }}
                  />
                </div>
                <div className={styles.ledgerCellCost}>
                  <CurrencyCell
                    id={`cost-original-${item.key}`}
                    labelText={showLabels ? 'Original Cost' : ''}
                    hideLabel={!showLabels}
                    disabled={isDestroyed}
                    value={item.costPerUnit}
                    onValueChange={(values, sourceInfo) => {
                      // Only react to real keystrokes. react-number-format also
                      // fires this on prop-driven reformatting (sourceInfo.event
                      // is undefined then), which must not write state back.
                      if (!sourceInfo.event) return;
                      // Empty clears to $0. While typing, a fragment like a lone
                      // "-" has a defined `value` but an undefined `floatValue`;
                      // fall back to the prior cost so it never stores NaN.
                      setItem(item.key, {
                        costPerUnit:
                          values.value === ''
                            ? 0
                            : (values.floatValue ?? item.costPerUnit),
                      });
                    }}
                  />
                </div>
                <div className={styles.ledgerCellCost}>
                  <CurrencyCell
                    id={`cost-total-${item.key}`}
                    labelText={showLabels ? 'Total Cost' : ''}
                    hideLabel={!showLabels}
                    disabled={isDestroyed}
                    value={
                      item.revisedTotal !== null
                        ? item.revisedTotal
                        : item.costPerUnit * item.quantity
                    }
                    onValueChange={(values, sourceInfo) => {
                      // Editing Original Cost changes this field's DERIVED value,
                      // which fires onValueChange with sourceInfo.event undefined.
                      // Ignoring it prevents that reformat from silently turning
                      // the derived total into a saved override.
                      if (!sourceInfo.event) return;
                      // Empty clears the override (falls back to cost × quantity).
                      // A mid-type fragment (undefined `floatValue`) keeps the
                      // current override rather than storing NaN.
                      setItem(item.key, {
                        revisedTotal:
                          values.value === ''
                            ? null
                            : (values.floatValue ?? item.revisedTotal),
                      });
                    }}
                  />
                </div>
                <div className={styles.ledgerCellAction}>
                  {isDestroyed ? (
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={Undo}
                      iconDescription="Undo Remove"
                      tooltipPosition="left"
                      onClick={() => setItem(item.key, { destroyed: false })}
                    />
                  ) : (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={TrashCan}
                      iconDescription="Remove"
                      tooltipPosition="left"
                      onClick={() => onDeleteLineItem(item)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={styles.emptyRow}>
          No cost line items yet. Add one below.
        </p>
      )}
      <div className={styles.ledgerFooter}>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          onClick={() => onAddLineItem(categoryOptions[0] ?? 'Drilling')}
        >
          Add Line Item
        </Button>
      </div>
    </div>
  );
};
